/**
 * Frontend logging service for Vigia.
 * Collects frontend logs and sends them to the server via the CLEF ingestion endpoint.
 */

type LogLevel = 'Debug' | 'Information' | 'Warning' | 'Error' | 'Fatal';

interface LogEntry {
  '@t': string;
  '@mt': string;
  '@l': LogLevel;
  '@x'?: string;
  Application: string;
  Component?: string;
  Action?: string;
  [key: string]: unknown;
}

class LoggerService {
  private static instance: LoggerService;
  private buffer: LogEntry[] = [];
  private flushTimer: number | null = null;
  private readonly maxBuffer = 50;
  private readonly flushInterval = 5000; // 5 seconds
  private isInitialized = false;

  // Store listener references for cleanup
  private readonly handleBeforeUnload = () => this.flush();
  private readonly handleError = (e: ErrorEvent) => {
    this.error('Uncaught error', {
      message: e.message,
      filename: e.filename,
      lineno: e.lineno,
      colno: e.colno,
    });
  };
  private readonly handleUnhandledRejection = (e: PromiseRejectionEvent) => {
    this.error('Unhandled promise rejection', {
      reason: String(e.reason),
    });
  };

  private constructor() {
    this.initialize();
  }

  private initialize(): void {
    if (this.isInitialized) return;

    this.startFlushTimer();

    window.addEventListener('beforeunload', this.handleBeforeUnload);
    window.addEventListener('error', this.handleError);
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);

    this.isInitialized = true;
  }

  static getInstance(): LoggerService {
    if (!LoggerService.instance) {
      LoggerService.instance = new LoggerService();
    }
    return LoggerService.instance;
  }

  private log(
    level: LogLevel,
    message: string,
    props?: Record<string, unknown>,
    error?: Error
  ): void {
    const entry: LogEntry = {
      '@t': new Date().toISOString(),
      '@mt': message,
      '@l': level,
      Application: 'Vigia-Front',
      UserAgent: navigator.userAgent,
      Url: window.location.href,
      ...props,
    };

    if (error) {
      entry['@x'] = `${error.name}: ${error.message}\n${error.stack || ''}`;
    }

    this.buffer.push(entry);

    // Flush immediately on errors or when buffer is full
    if (level === 'Error' || level === 'Fatal' || this.buffer.length >= this.maxBuffer) {
      this.flush();
    }
  }

  /**
   * Log a debug message.
   */
  debug(message: string, props?: Record<string, unknown>): void {
    this.log('Debug', message, props);
  }

  /**
   * Log an informational message.
   */
  info(message: string, props?: Record<string, unknown>): void {
    this.log('Information', message, props);
  }

  /**
   * Log a warning message.
   */
  warn(message: string, props?: Record<string, unknown>): void {
    this.log('Warning', message, props);
  }

  /**
   * Log an error message.
   */
  error(message: string, props?: Record<string, unknown>, err?: Error): void {
    this.log('Error', message, props, err);
  }

  /**
   * Log a fatal error message.
   */
  fatal(message: string, props?: Record<string, unknown>, err?: Error): void {
    this.log('Fatal', message, props, err);
  }

  /**
   * Log an API call.
   */
  logApiCall(method: string, url: string, status: number, duration: number): void {
    const level: LogLevel = status >= 500 ? 'Error' : status >= 400 ? 'Warning' : 'Debug';
    this.log(level, 'API call completed', {
      Method: method,
      Endpoint: url,
      StatusCode: status,
      DurationMs: duration,
    });
  }

  /**
   * Log a user action.
   */
  logAction(action: string, component: string, details?: Record<string, unknown>): void {
    this.info(`User action: ${action}`, {
      Action: action,
      Component: component,
      ...details,
    });
  }

  /**
   * Log navigation event.
   */
  logNavigation(from: string, to: string): void {
    this.debug('Navigation', {
      From: from,
      To: to,
    });
  }

  /**
   * Flush buffered logs to the server.
   */
  async flush(): Promise<void> {
    if (this.buffer.length === 0) return;

    const batch = this.buffer.splice(0, this.maxBuffer);
    const body = batch.map((e) => JSON.stringify(e)).join('\n');

    try {
      await fetch('/api/ingest/clef', {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.serilog.clef' },
        body,
        keepalive: true, // Ensures request completes even during page unload
      });
    } catch {
      // Re-enqueue events if there's space (avoid losing important logs)
      if (this.buffer.length < 200) {
        this.buffer.unshift(...batch);
      }
    }
  }

  private startFlushTimer(): void {
    if (this.flushTimer !== null) return;

    this.flushTimer = window.setInterval(() => {
      this.flush();
    }, this.flushInterval);
  }

  /**
   * Stop the flush timer. Call this when the app is unmounting.
   */
  stop(): void {
    if (this.flushTimer !== null) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    window.removeEventListener('error', this.handleError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    this.isInitialized = false;
    this.flush();
  }
}

export const logger = LoggerService.getInstance();
export default logger;
